import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { VideoViewReportRow, ReportsApi } from '@/services/reports.api';
import { AcademicClassesApi } from '@/services/academic.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

/**
 * Video View Report — one row per real YouTube lesson/topic video, matching
 * legacy's real shape (Subject/Lesson/Topic/Video Name/Total Views/Total
 * Duration/Watched Duration/Watched Percentage/Last Watched). Real watch
 * data is extremely concentrated (see docs/data-issues.md) — most rows here
 * legitimately show 0 views / N/A duration / N/A last watched. That's
 * accurate, not a bug. Applicable Students (current section headcount) is
 * always computed, independent of watch data.
 */
export function VideoViewReportPage() {
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-video-view-report'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];

  const { data: classSubjects = [] } = useQuery({
    queryKey: ['academic-class-subjects', 'for-video-view-report', classId],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  const subjects = Array.from(new Map(classSubjects.map((s) => [s.subjectId, s])).values());

  function handleClassChange(v: string) {
    setClassId(v);
    setSubjectId('');
  }

  const params = { classId: classId || undefined, subjectId: (classId && subjectId) || undefined };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['reports', 'video-view', params],
    queryFn: () => ReportsApi.videoViewReport(params),
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await ReportsApi.exportVideoViewReport(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Video View Report"
        description="Watch stats for real YouTube videos attached to lessons and topics."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={classId} onChange={(e) => handleClassChange(e.target.value)}>
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select
          className="!w-44"
          value={subjectId}
          onChange={(e) => setSubjectId(e.target.value)}
          disabled={!classId}
          title={classId ? undefined : 'Select a class to filter by subject'}
        >
          <option value="">All Subjects</option>
          {subjects.map((s) => (
            <option key={s.subjectId} value={s.subjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Lesson</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Topic</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Video Name</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Views</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Total Duration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Watched Duration</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Watched %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last Watched</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Applicable Students</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-12 text-center text-slate-400">
                    No YouTube videos found for the selected filters.
                  </td>
                </tr>
              ) : (
                rows.map((r: VideoViewReportRow) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-700">{r.subjectName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.lessonName}</td>
                    <td className="px-4 py-3 text-slate-700">{r.topicName ?? '—'}</td>
                    <td className="px-4 py-3 text-slate-700">{r.videoName}</td>
                    <td className="px-4 py-3 text-right">
                      <Badge tone={r.totalViews > 0 ? 'blue' : 'slate'}>{r.totalViews}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.totalDuration ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.watchedDuration ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.watchedPercentage ?? 0}%</td>
                    <td className="px-4 py-3 text-slate-700">{r.lastWatched ?? 'N/A'}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{r.applicableStudents}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
